import React, { useState, useRef } from 'react';
import { useUser } from '../../contextapi/UserContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import { Upload, Camera, Trash2, User, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

interface ProfilePictureUploaderProps {
  onUploadComplete?: (avatarUrl: string) => void;
  showTitle?: boolean;
}

export const ProfilePictureUploader: React.FC<ProfilePictureUploaderProps> = ({
  onUploadComplete,
  showTitle = true
}) => {
  const { user } = useUser();
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string>('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load existing avatar
  React.useEffect(() => {
    if (user?.id) {
      loadCurrentAvatar();
    }
  }, [user?.id]);

  const loadCurrentAvatar = async () => {
    if (!user?.id) return;

    try {
      const { supabase } = await import('../../utils/supabaseClient');
      
      const { data: profileData, error } = await supabase
        .rpc('get_user_profile_enhanced', { user_id: user.id });

      if (!error && profileData && profileData.length > 0) {
        const profile = profileData[0];
        const avatarPath = profile.avatar_url || '';
        
        // If avatar_path is stored as user.id/filename format, construct full URL
        if (avatarPath && !avatarPath.startsWith('http')) {
          const { data: { publicUrl } } = supabase.storage
            .from('avatars')
            .getPublicUrl(avatarPath);
          setAvatarUrl(publicUrl);
        } else {
          setAvatarUrl(avatarPath);
        }
      }
    } catch (error) {
      console.error('Error loading avatar:', error);
    }
  };

  const handleFileSelect = async (file: File) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid File Type',
        description: 'Please select an image file (JPEG, PNG, GIF, etc.)',
        variant: 'destructive',
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File Too Large',
        description: 'Please select an image smaller than 5MB',
        variant: 'destructive',
      });
      return;
    }

    await uploadAvatar(file);
  };

  const uploadAvatar = async (file: File) => {
    if (!user?.id) return;

    setUploading(true);
    try {
      const { supabase } = await import('../../utils/supabaseClient');

      // Create a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const filePath = `${user.id}/${fileName}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      // Update profile with new avatar path (user.id/filename format)
      const { error: updateError } = await supabase.rpc('update_user_profile_enhanced', {
        user_id: user.id,
        profile_avatar: filePath
      });

      if (updateError) {
        throw updateError;
      }

      setAvatarUrl(publicUrl);
      onUploadComplete?.(publicUrl);

      toast({
        title: 'Avatar Updated',
        description: 'Your profile picture has been successfully updated.',
      });
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast({
        title: 'Upload Failed',
        description: 'Failed to upload avatar. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const removeAvatar = async () => {
    if (!user?.id) return;

    setUploading(true);
    try {
      const { supabase } = await import('../../utils/supabaseClient');

      // Extract file path from avatarUrl for storage deletion
      let filePath = '';
      if (avatarUrl) {
        // Extract path from URL (e.g., "https://...supabase.co/storage/v1/object/public/avatars/path/to/file.jpg" -> "path/to/file.jpg")
        const urlParts = avatarUrl.split('/avatars/');
        if (urlParts.length > 1) {
          filePath = urlParts[1];
        }
      }

      // Delete file from storage bucket if file path exists
      if (filePath) {
        const { error: storageError } = await supabase.storage
          .from('avatars')
          .remove([filePath]);

        if (storageError) {
          console.warn('Error deleting file from storage:', storageError);
          // Continue with database update even if storage deletion fails
        }
      }

      // Update database to set avatar to empty string to clear it
      // Note: Passing null to the RPC function doesn't clear the field due to COALESCE logic
      // We need to pass an empty string to explicitly clear the avatar path
      const { error: updateError } = await supabase.rpc('update_user_profile_enhanced', {
        user_id: user.id,
        profile_avatar: ''  // Empty string to clear the avatar path in database
      });

      if (updateError) {
        throw updateError;
      }

      setAvatarUrl('');
      onUploadComplete?.('');

      toast({
        title: 'Avatar Removed',
        description: 'Your profile picture has been removed.',
      });
    } catch (error) {
      console.error('Error removing avatar:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove avatar. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const content = (
    <div className="flex flex-col items-center justify-center w-full">
      {/* Minimalistic Avatar Upload Area */}
      <div className="w-full max-w-sm">
        {/* Avatar with Integrated Upload */}
        <div
          className={`relative group cursor-pointer transition-all duration-200 ${
            dragOver ? 'scale-105' : 'hover:scale-105'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={triggerFileInput}
        >
          {/* Avatar Circle */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              {/* Main Avatar */}
              <div className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 rounded-full overflow-hidden border-4 border-gray-200 bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center shadow-lg transition-all duration-200 group-hover:border-blue-400 group-hover:shadow-xl">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="w-10 h-10 sm:w-14 sm:h-14 md:w-20 md:h-20 text-gray-400" />
                )}
              </div>

              {/* Upload Icon Overlay */}
              <div className="absolute inset-0 rounded-full bg-black bg-opacity-0 group-hover:bg-opacity-40 flex items-center justify-center transition-all duration-200">
                <Upload className="w-6 h-6 sm:w-8 sm:h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              </div>

              {/* Loading Spinner */}
              {uploading && (
                <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                </div>
              )}

              {/* Remove Button (Small X) */}
              {avatarUrl && !uploading && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeAvatar();
                  }}
                  className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-md transition-all duration-200 hover:scale-110"
                  title="Remove photo"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileInputChange}
            className="hidden"
            disabled={uploading}
          />

          {/* Upload Text */}
          <div className="text-center space-y-2">
            <div className="flex items-center justify-center space-x-2">
              <p className="text-sm sm:text-base font-medium text-gray-700">
                {avatarUrl ? 'Change Photo' : 'Upload Photo'}
              </p>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-4 h-4 sm:w-4 sm:h-4 text-blue-500 flex-shrink-0 mt-0.5 cursor-help hover:text-blue-600 transition-colors" />
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs text-left">
                    <p className="font-medium text-gray-700">Supported Formats</p>
                    <p className='mb-1'>PNG, JPG, GIF</p>
                    <p className="font-medium text-gray-700">File Size</p>
                    <p className='mb-1'>Maximum 5MB</p>
                    <p className="font-medium text-gray-700">Recommended</p>
                    <p className='mb-1'>Square image (1:1 ratio) for best results</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            
            <p className="text-xs sm:text-sm text-gray-500">
              Click or drag & drop to upload
            </p>
          </div>
        </div>

        {/* Mobile Action Button */}
        <div className="mt-4 sm:hidden">
          <Button
            type="button"
            onClick={triggerFileInput}
            disabled={uploading}
            className="w-full"
            size="sm"
          >
            <Camera className="w-4 h-4 mr-2" />
            {avatarUrl ? 'Change Photo' : 'Upload Photo'}
          </Button>
        </div>
      </div>
    </div>
  );

  if (showTitle) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            Profile Picture
          </CardTitle>
          <CardDescription>
            Upload a profile picture to personalize your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          {content}
        </CardContent>
      </Card>
    );
  }

  return content;
};
